/**
 * DetailDrawer — chrome wrapper around Fluent's drawers that enforces a
 * consistent header (title, subtitle, dismiss button) and padded body.
 *
 * Two variants:
 *   - `inline` (default): an `InlineDrawer` that sits in the layout flow and
 *     squeezes the content beside it. Use for persistent side panels opened
 *     from a row click (case detail, station detail, user drawer).
 *   - `overlay`: an `OverlayDrawer` that floats above the content with a
 *     backdrop and light dismiss (click outside / Esc). Use for drill-downs
 *     that should not reflow the table behind them (submission snapshot,
 *     invoice preview, session detail). Width comes from Fluent's `size`
 *     scale (`small` 320 / `medium` 592 / `large` 940 / `full`).
 *
 * Never hand-roll `Drawer` + `DrawerHeaderTitle` chrome in app code — this
 * component is the single source of drawer chrome (enforced by
 * scripts/check_frontend_design.py).
 *
 * For free-floating windows use the Workbench window manager. For inline
 * forms use `FormSection`.
 */
import type { ReactNode } from 'react';
import {
  Body1Strong,
  Button,
  Caption1,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  InlineDrawer,
  OverlayDrawer,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useLocale } from '../i18n/useLocale';

const useStyles = makeStyles({
  drawer: {
    flexShrink: 0,
  },
  start: {
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  end: {
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: 'auto',
  },
});

export type DetailDrawerVariant = 'inline' | 'overlay';
export type DetailDrawerSize = 'small' | 'medium' | 'large' | 'full';

interface DetailDrawerProps {
  open: boolean;
  onDismiss: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Side the drawer slides in from. Defaults to `end`. */
  position?: 'start' | 'end';
  /**
   * `inline` (default) sits in the layout flow; `overlay` floats above the
   * content with a backdrop + light dismiss.
   */
  variant?: DetailDrawerVariant;
  /** Width in pixels (inline variant). Defaults to 360. */
  width?: number;
  /** Fluent width scale for the overlay variant. Defaults to `medium`. */
  size?: DetailDrawerSize;
  /** Optional sticky footer (action buttons). */
  footer?: ReactNode;
  children: ReactNode;
}

export function DetailDrawer({
  open,
  onDismiss,
  title,
  subtitle,
  position = 'end',
  variant = 'inline',
  width = 360,
  size = 'medium',
  footer,
  children,
}: DetailDrawerProps) {
  const styles = useStyles();
  const { t } = useLocale();

  const chrome = (
    <>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              onClick={onDismiss}
              aria-label={t('common.close')}
            />
          }
        >
          <div className={styles.titleBlock}>
            <Body1Strong>{title}</Body1Strong>
            {subtitle ? <Caption1 className={styles.subtitle}>{subtitle}</Caption1> : null}
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </DrawerBody>
    </>
  );

  if (variant === 'overlay') {
    return (
      <OverlayDrawer
        open={open}
        position={position}
        size={size}
        onOpenChange={(_, data) => {
          /* Backdrop click / Esc — Fluent flips `open`; mirror to caller. */
          if (!data.open) onDismiss();
        }}
      >
        {chrome}
      </OverlayDrawer>
    );
  }

  return (
    <InlineDrawer
      open={open}
      position={position}
      style={{ width: `${width}px` }}
      className={mergeClasses(styles.drawer, position === 'start' ? styles.start : styles.end)}
    >
      {chrome}
    </InlineDrawer>
  );
}
